import { ChangeEvent, DragEvent, useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { pushLocalUserMessage, setBg, setDrawer, submitUserQuery } from "../store/chatSlice";
import { useAppDispatch } from "../store/hooks";
import "./Drawer.css";

const Drawer = () => {
  const dispatch = useAppDispatch();
  const [uploaded, setUploaded] = useState(false);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [isImage, setIsImage] = useState(false);
  const [isPdf, setIsPdf] = useState(false);
  const [fileTag, setFileTag] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const pdfCanvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
  }, []);

  const onFileSelected = async (selectedFile: File): Promise<void> => {
    setFile(selectedFile);
    setUploaded(true);
    setFileTag(selectedFile.name.split(".").pop()?.toUpperCase() ?? "");
    setIsImage(false);
    setIsPdf(false);
    setFilePreviewUrl("");

    if (selectedFile.type.startsWith("image/")) {
      setIsImage(true);
      const reader = new FileReader();
      reader.onload = (event) => setFilePreviewUrl(String(event.target?.result ?? ""));
      reader.readAsDataURL(selectedFile);
      return;
    }

    if (selectedFile.type === "application/pdf") {
      setIsPdf(true);
      const reader = new FileReader();
      reader.onload = async () => {
        const typedArray = new Uint8Array(reader.result as ArrayBuffer);
        const pdf = await pdfjsLib.getDocument(typedArray).promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = pdfCanvasRef.current;
        if (!canvas) {
          return;
        }
        const context = canvas.getContext("2d");
        if (!context) {
          return;
        }
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: context, viewport }).promise;
      };
      reader.readAsArrayBuffer(selectedFile);
    }
  };

  const close = (): void => {
    dispatch(setDrawer(false));
    dispatch(setBg(false));
  };

  const getSuggestions = async (): Promise<void> => {
    if (!file) {
      return;
    }
    dispatch(pushLocalUserMessage({ query: file.name, inputType: "TEXT", fileName: file.name }));
    dispatch(setDrawer(false));
    dispatch(setBg(false));
    await dispatch(submitUserQuery({ query: file.name, inputType: "TEXT", file }));
  };

  return (
    <div className="kMccIk">
      <div className="header">
        <p>Upload Resume</p>
        <span className="drawer-cross" onClick={close}>
          <i className="fa-solid fa-xmark" />
        </span>
      </div>
      <div className="content">
        <span style={{ fontSize: "15px", color: "#454545" }}>
          By uploading, you consent to us processing your data to generate recommendations. No data is stored.
        </span>
        <div
          className="upload-wrapper"
          onDrop={(event: DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            if (event.dataTransfer.files.length > 0) {
              void onFileSelected(event.dataTransfer.files[0]);
            }
          }}
          onDragOver={(event: DragEvent<HTMLDivElement>) => event.preventDefault()}
        >
          {!uploaded ? (
            <label className="upload-box">
              <i className="fa-solid fa-cloud-arrow-up upload-icon" />
              <p>
                <span className="upload-text">Drag or Browse file</span>
              </p>
              <input
                type="file"
                className="hidden-input"
                accept=".doc,.docx,.txt,.pdf,.png"
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const selected = event.target.files?.[0];
                  if (selected) {
                    void onFileSelected(selected);
                  }
                }}
              />
            </label>
          ) : (
            <div className="uploaded-box">
              <h3 className="upload-success">Document upload was successful!</h3>
              <div className="preview-card">
                <button className="close-btn" onClick={() => setUploaded(false)}>
                  x
                </button>
                {isImage ? <img src={filePreviewUrl ?? ""} alt="preview" className="preview-img" /> : null}
                {isPdf ? <canvas ref={pdfCanvasRef} className="preview-img" /> : null}
                <div>
                  {!isImage && !isPdf ? (
                    <img src="/txt.png" alt="file uploaded" className="preview-img txt" />
                  ) : null}
                  {fileTag !== "TXT" ? <span className="file-tag">{fileTag}</span> : null}
                </div>
              </div>
              <button className="cta-button" onClick={() => void getSuggestions()}>
                 Get suggestions
              </button>
            </div>
          )}
          <p className="supported-formats">(Supported formats: .doc, .docx, .txt, .pdf)</p>
        </div>
      </div>
    </div>
  );
};

export default Drawer;
